/**
 * editorMetaDirty.ts — whether the editor's right-rail metadata card holds
 * unsaved edits, shared from the rail to the page's agent-focus publish.
 *
 * The rail (`UniversalEditorRailMount`) edits SEO metadata into a local override
 * and saves it on an 800ms debounce, so between a keystroke and that save the
 * DB and the operator's buffer disagree. An agent `ed_set_canonical` (or a JSON-LD
 * / image tool) that PATCHes the same field in that window races the rail's save.
 *
 * This is the one bit of state the page needs from the rail to make the
 * `editor-meta` dirty veto real: a plain external store the rail writes and the
 * page reads through `useSyncExternalStore`, so a change re-publishes focus.
 * Deliberately not React context — the rail and the focus publish are far apart
 * in the tree and neither should re-render the other.
 */
import { useSyncExternalStore } from 'react';

let dirty = false;
const listeners = new Set<() => void>();

/** Called by the rail as its metadata buffer gains or clears unsaved edits. */
export function setEditorMetadataDirty(next: boolean): void {
  if (dirty === next) return;
  dirty = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return dirty;
}

/** Reactive read for the editor page's agent-focus publish. */
export function useEditorMetadataDirty(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
